import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="mb-8">
          <Link to="/">
            <Button variant="ghost" className="mb-6 -ml-2 text-white/70 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
          <div className="mb-6">
            <img 
              src="/images/logo-dark.svg" 
              alt="Sonary" 
              className="h-10 w-auto"
            />
          </div>
          <h1 className="text-4xl font-bold mb-2">Privacy Policy</h1>
          <p className="text-white/60">Last updated: February 2025</p>
        </div>

        {/* Content */}
        <div className="prose prose-invert max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white">1. Information We Collect</h2>
            <p className="text-white/80 leading-relaxed mb-4">
              When you use Sonary, we collect the following information:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-white/80">
              <li><strong>Account Information:</strong> Name, email address, and profile picture from your Google account when you sign in.</li>
              <li><strong>Activity Data:</strong> Albums you log, ratings, reviews, lists you create, and your social interactions (follows, likes, comments).</li>
              <li><strong>Usage Data:</strong> How you interact with our service to improve your experience.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white">2. How We Use Your Information</h2>
            <p className="text-white/80 leading-relaxed mb-4">
              We use your information to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-white/80">
              <li>Provide and maintain the Sonary service</li>
              <li>Display your activity to users who follow you</li>
              <li>Show personalized album recommendations</li>
              <li>Improve and develop new features</li>
              <li>Communicate with you about updates or support</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white">3. Data Sharing</h2>
            <p className="text-white/80 leading-relaxed">
              Your public activity (reviews, ratings, lists marked as public) is visible to other Sonary users. 
              We do <strong>not</strong> sell your personal information to third parties. We may share data with:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-white/80 mt-4">
              <li>Service providers who help us operate Sonary (hosting, authentication)</li>
              <li>Law enforcement when required by law</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white">4. Data Retention</h2>
            <p className="text-white/80 leading-relaxed">
              We retain your data for as long as your account is active. If you delete your account, 
              we will permanently delete all your personal data within 30 days, including:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-white/80 mt-4">
              <li>Your profile information</li>
              <li>All album logs, ratings, and reviews</li>
              <li>All lists you created</li>
              <li>Your follows and followers</li>
              <li>Your comments and likes</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white">5. Your Rights (GDPR)</h2>
            <p className="text-white/80 leading-relaxed mb-4">
              You have the following rights regarding your data:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-white/80">
              <li><strong>Right to Access:</strong> Export all your data from Settings → Export Data</li>
              <li><strong>Right to Rectification:</strong> Edit your profile information at any time</li>
              <li><strong>Right to Erasure:</strong> Delete your account and all data from Settings</li>
              <li><strong>Right to Portability:</strong> Download your data in JSON format</li>
              <li><strong>Right to Object:</strong> Control your privacy settings and what's visible</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white">6. Data Security</h2>
            <p className="text-white/80 leading-relaxed">
              We implement industry-standard security measures to protect your data, including:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-white/80 mt-4">
              <li>Encrypted data transmission (HTTPS)</li>
              <li>Secure authentication via Google OAuth</li>
              <li>Regular security reviews</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white">7. Cookies</h2>
            <p className="text-white/80 leading-relaxed">
              We use essential cookies to keep you logged in and maintain your session. 
              We do not use tracking cookies or third-party advertising cookies.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white">8. Children's Privacy</h2>
            <p className="text-white/80 leading-relaxed">
              Sonary is not intended for users under 13 years of age. We do not knowingly 
              collect data from children under 13.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white">9. Changes to This Policy</h2>
            <p className="text-white/80 leading-relaxed">
              We may update this privacy policy from time to time. We will notify you of 
              any significant changes by posting the new policy on this page and updating 
              the "Last updated" date.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white">10. Contact Us</h2>
            <p className="text-white/80 leading-relaxed">
              If you have any questions about this Privacy Policy or your data, please contact us at:
            </p>
            <p className="text-primary mt-2">privacy@sonary.app</p>
          </section>
        </div>

        {/* Footer */}
        <div className="mt-16 pt-8 border-t border-white/10">
          <div className="flex items-center justify-between">
            <img 
              src="/images/logo-dark.svg" 
              alt="Sonary" 
              className="h-6 w-auto"
            />
            <p className="text-sm text-white/50">© 2025 Sonary. All rights reserved.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
